/**
 * Thread context analysis
 * Analyzes thread characteristics to provide scoring context
 */

import type { GmailMessage } from '../types';
import { normalizeEmail, logScoringDecision, clampScore } from './utils';

export interface ThreadContextResult {
  score: number;
  reasons: string[];
  threadSize: number | null;
  isLargeThread: boolean;
  isSingleMessageThread: boolean;
  userStartedThread: boolean;
}

/**
 * Analyze thread context and return scoring adjustment
 * @param message - Gmail message data
 * @param threadHistory - Thread history array
 * @param threadSize - Number of messages in thread (if available)
 * @param context - Optional context for logging
 * @returns Score adjustment and reasons
 */
export function analyzeThreadContext(
  message: GmailMessage,
  threadHistory: string[] = [],
  threadSize: number | null = null,
  context?: {
    userId?: string;
    messageId?: string;
  }
): ThreadContextResult {
  let score = 0;
  const reasons: string[] = [];

  const hasThreadHistory = threadHistory.length > 0;
  const userStartedThread = threadHistory.includes('user_sent');
  // threadSize is null on the real scoring path (not available in the basic Gmail
  // message format), so fall back to the threadHistory length to detect a large
  // ongoing thread — otherwise this trust signal never fires.
  const effectiveThreadSize =
    threadSize ?? (threadHistory.length > 0 ? threadHistory.length : null);
  const isLargeThread = effectiveThreadSize !== null && effectiveThreadSize >= 10;
  const isSingleMessageThread = threadSize === 1 || (!hasThreadHistory && threadSize === null);

  // Large threads (10+ messages) - ongoing conversation, reduce score
  if (isLargeThread) {
    score = -1;
    reasons.push(
      `Large thread (${effectiveThreadSize} messages) - ongoing conversation, trust signal`
    );
  }

  // Single message threads - slight penalty if combined with other indicators
  // Note: We don't add penalty here by default, only if combined with other spam indicators
  // This is handled by the caller based on other factors
  if (isSingleMessageThread && !userStartedThread) {
    // Only note it, don't penalize yet
    reasons.push(`Single message thread (no previous conversation)`);
  }

  // User started thread - already handled via previous contact trust signal
  // But we can note it for context
  if (userStartedThread) {
    reasons.push(`User started this thread`);
  }

  // Clamp score to prevent extreme values
  score = clampScore(score, -1, 0);

  // Log decision if there's a score adjustment
  if (score !== 0) {
    logScoringDecision({
      userId: context?.userId,
      messageId: context?.messageId,
      senderEmail: normalizeEmail(message.from),
      checkName: 'thread_context',
      score,
      reason: reasons.join('; '),
    });
  }

  return {
    score,
    reasons,
    threadSize,
    isLargeThread,
    isSingleMessageThread,
    userStartedThread,
  };
}

/**
 * Analyze thread context with detailed results for trait tracking
 */
export function analyzeThreadContextWithDetails(
  message: GmailMessage,
  threadHistory: string[] = [],
  threadSize: number | null = null,
  context?: {
    userId?: string;
    messageId?: string;
  }
): ThreadContextResult & {
  details: {
    hasThreadHistory: boolean;
    threadId: string;
  };
} {
  const result = analyzeThreadContext(message, threadHistory, threadSize, context);

  return {
    ...result,
    details: {
      hasThreadHistory: threadHistory.length > 0,
      threadId: message.thread_id || '',
    },
  };
}
